<template>
  <div
    v-if="needRefresh"
    class="tabletPortrait:top-14 tabletPortrait:translate-y-0 absolute top-1/2 left-1/2 z-50 flex -translate-x-1/2 -translate-y-1/2 transform justify-center px-4"
  >
    <div
      class="shadow-modal-m bg-tm-gray-900 tabletPortrait:max-w-mobileLandscape flex w-full flex-col gap-3 rounded-xl p-5"
      role="alert"
    >
      <!-- Header: message + actions -->
      <div class="flex w-full items-center justify-between gap-3">
        <span class="text-sm font-bold text-white"> Eine neue Version steht zur Verfügung. </span>

        <div class="flex shrink-0 items-center gap-2.5">
          <GenericButton
            text="Update"
            size="small"
            icon="icon-[mingcute--refresh-1-line]"
            icon-size="size-3"
            :color-variant="ColorVariants.MOST_IMPORTANT"
            :loading="loading"
            btn-class="disabled:!bg-tm-petrol-base disabled:!text-white"
            @confirm-click="reload()"
          />
          <button
            type="button"
            class="flex cursor-pointer items-center text-white disabled:opacity-50"
            aria-label="Hinweis schließen"
            :disabled="loading"
            @click="close()"
          >
            <GenericIcon icon="icon-[mingcute--close-line]" icon-size="size-4.5" />
          </button>
        </div>
      </div>

      <!-- Status: update stuck / failed -->
      <p v-if="stuck && !forcedReloadAttempted" class="text-center text-xs text-white">
        Aktualisierung hängt fest.
        <button type="button" class="underline" @click="forceReload">Erneut laden</button>
      </p>
      <p v-else-if="updateFailed" class="text-tm-red-highlight text-center text-xs">
        Update fehlgeschlagen.
        <button type="button" class="underline" @click="reload">Nochmal versuchen</button>
      </p>
    </div>
  </div>
</template>

<script setup lang="ts">
import { logger } from '@/lib/logger';
import { isMobileSafari } from '@src/helpers/getBrowserDetails';
import { ColorVariants } from '@therame/design-tokens/src/color-variants';
import { GenericButton, GenericIcon } from '@therame/ui';
import { useRegisterSW } from 'virtual:pwa-register/vue';
import { nextTick, onBeforeUnmount, onMounted, ref } from 'vue';

const loading = ref<boolean>(false);
const stuck = ref<boolean>(false); // true if controllerchange didn't happen in time
const updateFailed = ref<boolean>(false);
const forcedReloadAttempted = ref<boolean>(false);

// How long we wait for the new SW to take control before offering a forced reload (Safari/iOS quirk)
const FORCE_RELOAD_TIMEOUT = 10000; // 10s
let forceReloadTimer: ReturnType<typeof setTimeout> | undefined;
let controllerChangeHandled = false;
const MIN_SPINNER_TIME = 300; // ms minimal visual feedback
let spinnerStart = 0;

const period = 60 * 5 * 1000; // 5 minutes
const swActivated = ref<boolean>(false);
const registration = ref<ServiceWorkerRegistration>();

/**
 * This function will register a periodic sync check every hour, you can modify the interval as needed.
 * @param {string} swUrl
 * @param {ServiceWorkerRegistration} r
 */
function registerPeriodicSync(swUrl: string, r: ServiceWorkerRegistration) {
  if (period <= 0) return;

  setInterval(async () => {
    if ('onLine' in navigator && !navigator.onLine) return;

    const resp = await fetch(swUrl, {
      cache: 'no-store',
      headers: {
        cache: 'no-store',
        'cache-control': 'no-cache',
      },
    });

    if (resp?.status === 200) await r.update();
  }, period);
}

const {
  // flag that tells us an update is waiting
  needRefresh,
  updateServiceWorker,
} = useRegisterSW({
  immediate: true,
  onRegisteredSW(swUrl, r) {
    registration.value = r;
    if (period <= 0) return;
    if (r?.active?.state === 'activated') {
      swActivated.value = true;
      registerPeriodicSync(swUrl, r);
    } else if (r?.installing) {
      r.installing.addEventListener('statechange', (e) => {
        const sw: ServiceWorker = e.target as any;
        swActivated.value = sw.state === 'activated';
        if (swActivated.value) {
          registerPeriodicSync(swUrl, r);
        }
      });
    }
  },
});

const reload = async () => {
  loading.value = true;
  spinnerStart = performance.now();
  stuck.value = false;
  updateFailed.value = false;
  forcedReloadAttempted.value = false;

  try {
    // Force a paint before heavy SW update logic so the spinner is actually visible, especially on iOS.
    await nextTick();
    await new Promise((r) => requestAnimationFrame(() => r(null)));
    // Trigger the service worker update. Passing `true` lets the vite-plugin-pwa helper handle
    // reloading the page AFTER the new SW has taken control (listens to `controllerchange`).
    await updateServiceWorker(true);
  } catch (e) {
    logger.error({ msg: 'PWA update failed:', error: e });
    updateFailed.value = true;
  } finally {
    // We deliberately do NOT force a manual reload here; the helper already does it
    // safely once the new SW is the controller. Just release the button state.
    // Instead of immediately clearing loading, we keep spinner until controllerchange OR timeout
    // to make user aware something is happening. We'll clear on events below.
    startControllerChangeWatchdog();
  }
};

const close = () => {
  needRefresh.value = false;
};

function startControllerChangeWatchdog() {
  // Clear any previous timer
  if (forceReloadTimer) clearTimeout(forceReloadTimer);

  // If controllerchange doesn't fire (Safari/iOS pinned PWAs sometimes), show fallback.
  forceReloadTimer = setTimeout(() => {
    if (!controllerChangeHandled) {
      stuck.value = true;
      loading.value = false; // allow interaction again
      logger.warn('[PWA] Update appears stuck - offering forced reload.');
    }
  }, FORCE_RELOAD_TIMEOUT);
}

function onControllerChange() {
  controllerChangeHandled = true;
  if (forceReloadTimer) clearTimeout(forceReloadTimer);
  const elapsed = performance.now() - spinnerStart;
  const remaining = MIN_SPINNER_TIME - elapsed;
  if (remaining > 0) {
    setTimeout(() => {
      loading.value = false;
    }, remaining);
  } else {
    loading.value = false;
  }
  logger.debug('[PWA] controllerchange detected – new SW active.');
}

onMounted(() => {
  if (navigator?.serviceWorker) {
    navigator.serviceWorker.addEventListener('controllerchange', onControllerChange);
  }
});

onBeforeUnmount(() => {
  if (navigator?.serviceWorker) {
    navigator.serviceWorker.removeEventListener('controllerchange', onControllerChange);
  }
  if (forceReloadTimer) clearTimeout(forceReloadTimer);
});

const forceReload = () => {
  forcedReloadAttempted.value = true;
  logger.warn('[PWA] Forcing full page reload (possible stale SW).');
  window.location.reload();
};
</script>
